import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  MANNER_CHOICES,
  MANNERS,
  type DraftVoiceDto,
  type Manner,
  type MannerChoice,
  type RenderDraftStateDto,
  type StartVoiceoverResult,
  type VoiceoverDto,
} from '@app/shared';
import { AudioPlayer, LinkAudioPlayer } from '../components/AudioPlayer';
import { HelpHint } from '../components/HelpHint';
import { SampleButton, useSamplePlayer } from '../components/SampleButton';
import { StepAction, StepLayout } from '../components/shell/StepLayout';
import { StockVoiceList } from '../components/StockVoiceList';
import { Button, Card, Chip, Segmented, Skeleton, SkeletonGroup, Spinner, Toast } from '../components/ui';
import { useT } from '../i18n';
import { answerOf, priceParts } from '../lib/build-panel';
import { canBuyNow } from '../lib/payment-options';
import { creditsCount } from '../lib/format';
import { renderApi } from '../lib/render-client';
import { haptic } from '../lib/tma';
import { voiceApi } from '../lib/voice-client';
import { warmUp } from '../lib/warm';
import { checkIssues, shortIssues, voiceIssueText } from '../lib/voice-format';
import {
  affordLine,
  buildStep,
  defaultVoiceChoice,
  readyTakeOf,
  resolvedManner,
  sampleLine,
  sampleOf,
  startingChoice,
  takeChoiceOf,
  takesLine,
  totalLine,
  type TakeChoice,
  type VoiceChoice,
} from '../lib/voice-screen';

type Note = { text: string; link: { to: string; label: string } | null };

const linkButton =
  'flex items-center justify-center h-10 w-full rounded-full bg-ink text-ink-on text-sm font-semibold transition-transform active:scale-[0.97]';

/**
 * «Озвучка» of a reel (spec 5): a ready-made voice, my voice or my own reading;
 * the manner with samples; «Послушать с моим текстом»; the music; the total and
 * «Собрать». A take never starts by itself: only the listen button and «Собрать»
 * start one, and the same text, voice and manner replay the ready take for free.
 */
export function VoicePage() {
  const t = useT();
  const v = t.voiceScreen;
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const draftId = Number(useParams().draftId);
  const valid = Number.isInteger(draftId) && draftId > 0;
  const voiceKey = ['draft-voice', draftId];
  const stateKey = ['render-draft', draftId];
  const player = useSamplePlayer();

  const voiceQuery = useQuery<DraftVoiceDto>({
    queryKey: voiceKey,
    queryFn: () => voiceApi.getDraftVoice(draftId),
    enabled: valid,
    staleTime: 0,
    refetchInterval: (q) => (q.state.data?.working ? 2000 : false),
  });
  const stateQuery = useQuery<RenderDraftStateDto>({
    queryKey: stateKey,
    queryFn: () => renderApi.getDraftState(draftId),
    enabled: valid,
    staleTime: 0,
  });
  const voice = voiceQuery.data;
  const state = stateQuery.data;

  const [choice, setChoice] = useState<VoiceChoice | null>(null);
  const lastStock = useRef<string | null>(null);
  const [mannerChoice, setMannerChoice] = useState<MannerChoice>('auto');
  const [music, setMusic] = useState<'auto' | 'none'>('auto');
  const [note, setNote] = useState<Note | null>(null);
  const [starting, setStarting] = useState(false);
  const [building, setBuilding] = useState(false);
  /** The take this screen started: its failure is said here. */
  const [lastTakeId, setLastTakeId] = useState<number | null>(null);
  const started = useRef(false);

  // A visit starts on what the person chose before (the take at work, else the newest ready take);
  // later a voice the owner switched off falls back to the first one.
  useEffect(() => {
    if (!voice) return;
    if (!started.current) {
      started.current = true;
      const asked: VoiceChoice | undefined = params.get('voice') === 'own' ? { source: 'own' } : undefined;
      const start = startingChoice(voice, asked);
      if (start.choice.source === 'stock') lastStock.current = start.choice.voice;
      setChoice(start.choice);
      setMannerChoice(start.manner);
      return;
    }
    setChoice((c) => {
      if (!c) return c;
      const next = defaultVoiceChoice(voice, c);
      if (next.source === 'stock') lastStock.current = next.voice;
      return JSON.stringify(c) === JSON.stringify(next) ? c : next;
    });
  }, [voice, params]);

  const manner = resolvedManner(mannerChoice, voice?.manner ?? null);
  useEffect(() => player.stop(), [choice, manner]);
  // The samples of the chosen stock voice come ahead of ▶ (each ~60 KB, once per session;
  // their links never change, unlike the signed ones of a clone).
  useEffect(() => {
    if (!voice || choice?.source !== 'stock') return;
    const stock = voice.stock.find((s) => s.id === choice.voice);
    warmUp(MANNERS.map((m) => (stock?.samples[m] ? renderApi.absoluteUrl(stock.samples[m]!) : null)));
  }, [voice, choice]);

  const lastTake = useQuery<VoiceoverDto>({
    queryKey: ['voiceover', lastTakeId],
    queryFn: () => voiceApi.getVoiceover(lastTakeId!),
    enabled: lastTakeId !== null,
    refetchInterval: (q) => (q.state.data?.status === 'working' ? 2000 : false),
  });
  useEffect(() => {
    if (lastTake.data && lastTake.data.status !== 'working') void qc.invalidateQueries({ queryKey: voiceKey });
  }, [lastTake.data?.status]);

  /**
   * «Собрать» with the ready take, or with the voice and manner chosen now (spec 5.6, 0710):
   * then the server voices and builds, and the result screen opens at once — the person may
   * close the Mini App, the reel comes to the chat.
   */
  const build = async (by: { voiceoverId: number } | { take: TakeChoice }) => {
    if (!state || !choice) return;
    const style = state.style ?? state.defaultStyle;
    if (!style) return;
    setBuilding(true);
    setNote(null);
    try {
      const res = await renderApi.startBuild({ draftId, style, animated: true, music, voice: choice.source, ...by });
      const a = answerOf(res, draftId, t, { canBuy: res.kind === 'no_credits' && (await canBuyNow(qc)) });
      if (a.kind === 'navigate') {
        haptic('success');
        navigate(a.to);
      } else if (a.kind === 'note') setNote({ text: a.text, link: a.link });
      else void qc.invalidateQueries({ queryKey: stateKey });
    } catch {
      setNote({ text: t.common.failed, link: null });
    } finally {
      setBuilding(false);
    }
  };

  const answerTake = (res: StartVoiceoverResult): VoiceoverDto | null => {
    if (res.kind === 'ok') {
      qc.setQueryData(['voiceover', res.voiceover.id], res.voiceover);
      setLastTakeId(res.voiceover.id);
      void qc.invalidateQueries({ queryKey: voiceKey });
      return res.voiceover;
    }
    if (res.kind === 'limit') setNote({ text: res.reason === 'per_draft' ? v.takesOver : v.dailyOver, link: null });
    else if (res.kind === 'voice_unknown') setNote({ text: v.voiceGone, link: null });
    else if (res.kind === 'no_voice') setNote({ text: v.noClone, link: null });
    else setNote({ text: t.common.failed, link: null });
    void qc.invalidateQueries({ queryKey: voiceKey });
    return null;
  };

  /**
   * «Послушать с моим текстом» or «Собрать» without a ready take: one take, counted (spec 5.4).
   * `fresh` — «Переозвучить»: a new take even though a ready one of this choice is there.
   */
  const startTake = async (fresh = false): Promise<VoiceoverDto | null> => {
    if (!choice || choice.source === 'own') return null;
    setStarting(true);
    setNote(null);
    const again = fresh ? { fresh: true } : {};
    try {
      const res =
        choice.source === 'stock'
          ? await voiceApi.startStockVoiceover(draftId, { voice: choice.voice, manner: mannerChoice, ...again })
          : await voiceApi.startCloneVoiceover(draftId, { manner: mannerChoice, ...again });
      return answerTake(res);
    } catch {
      setNote({ text: t.common.failed, link: null });
      return null;
    } finally {
      setStarting(false);
    }
  };

  if (!valid) return <Navigate to="/" replace />;
  if (state && state.format === 'carousel') return <Navigate to={`/drafts/${draftId}`} replace />;

  const page = (content: JSX.Element) => (
    <StepLayout title={v.screenTitle} back={`/drafts/${draftId}`}>
      {content}
    </StepLayout>
  );

  if (voiceQuery.isError || stateQuery.isError) {
    return page(
      <Card variant="surface" padding="lg" className="text-center">
        <p className="text-sm">{t.common.failed}</p>
      </Card>,
    );
  }
  if (!voice || !state || !choice) {
    return page(
      <SkeletonGroup label={t.common.loading} className="space-y-3">
        <Skeleton className="h-11" />
        <Skeleton className="h-56" />
        <Skeleton className="h-24" />
      </SkeletonGroup>,
    );
  }

  const ready = readyTakeOf(voice, choice, manner);
  const working = voice.working;
  // A take this screen started failed — also a «Переозвучить» while the older take is still there.
  const failedHere =
    lastTake.data?.status === 'failed' && !working && (!ready || lastTake.data.id > ready.id) ? lastTake.data : null;
  const step = buildStep(voice, choice, manner);
  const quote =
    choice.source === 'stock' ? state.quote.stockVoice : choice.source === 'own' ? state.quote.ownVoice : state.quote.animated;
  const parts = priceParts(quote, t);
  const voicing = starting || working !== null;
  const back = encodeURIComponent(`/drafts/${draftId}/voice`);
  const synthesized = choice.source === 'stock' || (choice.source === 'clone' && voice.voice === 'ready');
  const sample = sampleOf(voice, choice, manner);
  const line = sampleLine(voice, choice, manner, t);

  const onBuild = async () => {
    haptic('medium');
    if (state.activeJobId !== null) return navigate(`/renders/${state.activeJobId}`);
    switch (step.kind) {
      case 'build':
        return build({ voiceoverId: step.voiceoverId });
      case 'record_own':
        return navigate(`/drafts/${draftId}/own`);
      case 'no_clone':
        return setNote({ text: v.noClone, link: null });
      case 'limit':
        return setNote({ text: v.takesOver, link: null });
      // No ready take: the server starts (or joins) it and builds when it is ready.
      case 'wait':
      case 'voice_first': {
        const take = takeChoiceOf(choice, mannerChoice);
        if (take) return build({ take });
        return;
      }
    }
  };

  const own = voice.own;
  const buildLabel =
    state.activeJobId !== null ? t.render.activeBuild : quote.free ? v.buildFree : v.build(creditsCount(quote.price));

  return page(
    <div className="space-y-5">
      {!state.approved && (
        <Toast tone="warning">
          {t.render.notApproved}{' '}
          <Link to={`/drafts/${draftId}`} className="underline font-medium">
            {t.render.toText}
          </Link>
        </Toast>
      )}

      <section>
        <h2 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.06em] text-hint mb-2">
          {v.voice}
          <HelpHint topic="voice" />
        </h2>
        <Segmented<VoiceChoice['source']>
          className="mb-3"
          value={choice.source}
          options={[
            { value: 'stock', label: v.stock },
            { value: 'clone', label: v.mine },
            { value: 'own', label: v.own },
          ]}
          onChange={(source) =>
            setChoice(
              source === 'stock'
                ? { source, voice: lastStock.current ?? voice.stock[0]?.id ?? '' }
                : { source },
            )
          }
        />

        {choice.source === 'stock' && (
          <StockVoiceList
            voices={voice.stock}
            chosen={choice.voice}
            manner={manner}
            player={player}
            disabled={voicing}
            onChoose={(id) => {
              lastStock.current = id;
              setChoice({ source: 'stock', voice: id });
            }}
          />
        )}

        {choice.source === 'clone' && (
          <Card variant="surface" padding="md" className="space-y-3">
            {voice.voice === 'none' && (
              <>
                <p className="text-sm">
                  {v.noClone} <HelpHint topic="myVoice" />
                </p>
                <Link to={`/voice/record?back=${back}`} className={linkButton}>
                  {v.recordSample}
                </Link>
              </>
            )}
            {voice.voice === 'pending' && (
              <>
                <p className="text-sm">{v.confirmClone}</p>
                <Link to={`/voice/record?step=preview&back=${back}`} className={linkButton}>
                  {v.listenClone}
                </Link>
              </>
            )}
            {voice.voice === 'ready' && sample?.kind === 'clone' && (
              <div className="flex items-center gap-3">
                <SampleButton
                  player={player}
                  sampleKey={`clone:${manner}`}
                  source={
                    voice.cloneSampleUrls
                      ? renderApi.absoluteUrl(voice.cloneSampleUrls[manner])
                      : () => voiceApi.getCloneSample(manner)
                  }
                />
                <span className="text-sm font-medium">{v.mine}</span>
              </div>
            )}
          </Card>
        )}

        {choice.source === 'own' && (
          <Card variant="surface" padding="md" className="space-y-3">
            {own?.status === 'ready' && (
              <>
                <p className="text-sm">{t.voiceover.ownListen}</p>
                {own.audioUrl ? (
                  <LinkAudioPlayer key={own.id} src={renderApi.absoluteUrl(own.audioUrl)} />
                ) : (
                  <AudioPlayer queryKey={['voiceover-audio', own.id]} load={() => voiceApi.getVoiceoverAudio(own.id)} />
                )}
                <TakeCheckNote take={own} />
              </>
            )}
            {own?.status === 'rejected' && <TakeCheckNote take={own} title={t.voiceover.ownRejected} />}
            {!own && <p className="text-sm text-hint">{t.voiceover.ownHint}</p>}
            <Link to={`/drafts/${draftId}/own`} className={linkButton}>
              {own ? t.voiceover.ownRerecord : t.voiceover.ownRecord}
            </Link>
          </Card>
        )}
      </section>

      {choice.source !== 'own' && (
        <section>
          <h2 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.06em] text-hint mb-2">
            {v.manner}
            <HelpHint topic="manner" />
          </h2>
          <div className="flex flex-wrap gap-2">
            {MANNER_CHOICES.map((m) => (
              <Chip key={m} selected={mannerChoice === m} disabled={voicing} onClick={() => setMannerChoice(m)}>
                {t.voiceover.manners[m]}
              </Chip>
            ))}
          </div>
          {line && <p className="text-xs text-hint mt-2">{line}</p>}
        </section>
      )}

      {synthesized && (
        <section className="space-y-2">
          {voice.textChanged && !ready && <p className="text-sm">{v.textChanged}</p>}
          {failedHere && !voicing && (
            <p className="text-sm">
              {failedHere.error === 'voice_mismatch'
                ? t.voiceover.failed.voice_mismatch
                : failedHere.error === 'unavailable'
                  ? t.voiceover.failed.unavailable
                  : t.voiceover.failed.other}
            </p>
          )}
          {voicing ? (
            <p className="flex items-center gap-2 py-2 text-sm text-hint">
              <Spinner size="sm" />
              {v.voicing}
            </p>
          ) : ready ? (
            <>
              {ready.audioUrl ? (
                <LinkAudioPlayer key={ready.id} src={renderApi.absoluteUrl(ready.audioUrl)} />
              ) : (
                <AudioPlayer queryKey={['voiceover-audio', ready.id]} load={() => voiceApi.getVoiceoverAudio(ready.id)} />
              )}
              <TakeCheckNote take={ready} />
              <Button
                variant="secondary"
                fullWidth
                disabled={voice.takesLeft === 0 || !state.approved}
                onClick={() => {
                  haptic('light');
                  void startTake(true);
                }}
              >
                {t.voiceover.revoice}
              </Button>
            </>
          ) : (
            <Button
              variant="secondary"
              fullWidth
              disabled={voice.takesLeft === 0 || !state.approved}
              onClick={() => {
                haptic('light');
                void startTake();
              }}
            >
              {v.listen}
            </Button>
          )}
          {voice.takesLeft === 0 && !ready ? (
            <p className="text-xs text-hint">{v.takesOver}</p>
          ) : (
            takesLine(voice, t) && (
              <p className="flex items-center gap-1.5 text-xs text-hint">
                {takesLine(voice, t)}
                <HelpHint topic="takes" />
              </p>
            )
          )}
        </section>
      )}

      <section>
        <h2 className="text-xs font-bold uppercase tracking-[0.06em] text-hint mb-2">{v.music}</h2>
        <div className="grid grid-cols-2 gap-2">
          <Chip selected={music === 'auto'} onClick={() => setMusic('auto')} className="justify-center">
            {v.musicOn}
          </Chip>
          <Chip selected={music === 'none'} onClick={() => setMusic('none')} className="justify-center">
            {v.musicOff}
          </Chip>
        </div>
      </section>

      {note && (
        <Toast tone="warning">
          {note.text}
          {note.link && (
            <>
              {' '}
              <Link to={note.link.to} className="underline font-medium">
                {note.link.label}
              </Link>
            </>
          )}
        </Toast>
      )}

      <StepAction>
        <div className="mb-2">
          <p className="flex items-center gap-1.5 text-sm font-semibold">
            {totalLine(quote, state.freeRebuilds, t)}
            <HelpHint topic="credits" />
          </p>
          <p className="text-xs text-hint">{affordLine(state.balance, quote.free ? 0 : quote.price, 'reel', t)}</p>
          {parts && <p className="text-xs text-hint mt-0.5">{parts}</p>}
        </div>
        <Button
          variant="primary"
          size="lg"
          fullWidth
          disabled={!state.approved || building || (step.kind === 'limit' && state.activeJobId === null)}
          loading={building}
          onClick={() => void onBuild()}
        >
          {buildLabel}
        </Button>
      </StepAction>
    </div>,
  );
}

/**
 * Under a take: the places the check heard otherwise (2026-10-05: the person decides, not
 * the check) — words to listen to, or a warning that a piece is lost; `title` — a rejected
 * own reading says it always, even without places to name.
 */
function TakeCheckNote({ take, title }: { take: VoiceoverDto; title?: string }) {
  const t = useT();
  const { shown, more } = shortIssues(checkIssues(take));
  if (shown.length === 0 && !title) return null;
  return (
    <div className="space-y-1">
      <p className="text-sm">{title ?? (take.check === 'gross' ? t.voiceover.checkGross : t.voiceover.checkMinor)}</p>
      {shown.length > 0 && (
        <ul className="text-sm list-disc pl-5 space-y-1">
          {shown.map((i, n) => (
            <li key={n}>{voiceIssueText(i, t)}</li>
          ))}
        </ul>
      )}
      {more > 0 && <p className="text-xs text-hint">{t.voiceover.moreIssues(more)}</p>}
    </div>
  );
}
